import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useColorModeValue,
  Collapse,
  Code,
  Progress,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiCheckCircle, FiAlertCircle, FiClock } from 'react-icons/fi';
import moment from 'moment';
import { supabase, isSupabaseConfigured, transformImportHistoryToCamelCase } from '../../../utils/supabase';

const STATUS_COLORS = {
  processing: 'blue',
  completed: 'green',
  partial: 'yellow',
  failed: 'red',
};

const STATUS_ICONS = {
  processing: FiClock,
  completed: FiCheckCircle,
  partial: FiAlertCircle,
  failed: FiAlertCircle,
};

export default function ImportHistoryViewer() {
  const [imports, setImports] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const expandedBG = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchImports = async () => {
      try {
        const { data, error } = await supabase
          .from('crm_import_history')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;

        const importsList = (data || []).map(transformImportHistoryToCamelCase);
        setImports(importsList);
      } catch (error) {
        console.error('Error fetching import history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImports();

    // Set up real-time subscription
    const channel = supabase
      .channel(`import-history-changes-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'crm_import_history' 
      }, () => {
        fetchImports();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const toggleRow = (importId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(importId)) {
        newSet.delete(importId);
      } else {
        newSet.add(importId);
      }
      return newSet;
    });
  };

  const getProgressPercentage = (importJob) => {
    if (!importJob.totalRows || importJob.totalRows === 0) return 0;
    const processed = (importJob.importedRows || 0) + (importJob.updatedRows || 0) + (importJob.failedRows || 0);
    return Math.min(100, Math.round((processed / importJob.totalRows) * 100));
  };

  if (loading) {
    return <Text>Loading import history...</Text>;
  }

  if (imports.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No import history yet</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize="sm" fontWeight="semibold" mb={3}>
        Recent Imports (Last 20)
      </Text>
      <Box overflowX="auto" borderWidth="1px" borderRadius="md" borderColor={borderColor}>
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBG}>
            <Tr>
              <Th width="40px"></Th>
              <Th>Started</Th>
              <Th>Status</Th>
              <Th>File / By</Th>
              <Th>Rows</Th>
              <Th>Created</Th>
              <Th>Updated</Th>
              <Th>Errors</Th>
              <Th>Progress</Th>
            </Tr>
          </Thead>
          <Tbody>
            {imports.map((importJob) => {
              const StatusIcon = STATUS_ICONS[importJob.status] || FiClock;
              const progressPct = getProgressPercentage(importJob);
              // errors is JSONB: either a legacy array or { errors, warnings }
              const errorList = Array.isArray(importJob.errors)
                ? importJob.errors
                : (importJob.errors?.errors || []);
              const warningList = Array.isArray(importJob.errors)
                ? []
                : (importJob.errors?.warnings || []);

              return (
                <React.Fragment key={importJob.id}>
                  <Tr _hover={{ bg: hoverBG }}>
                    <Td>
                      <IconButton
                        icon={expandedRows.has(importJob.id) ? <FiChevronUp /> : <FiChevronDown />}
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleRow(importJob.id)}
                        aria-label="Toggle details"
                      />
                    </Td>
                    <Td fontSize="xs">
                      {importJob.createdAt
                        ? moment(importJob.createdAt).format('MM/DD/YY HH:mm')
                        : 'N/A'}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={STATUS_COLORS[importJob.status]}
                        fontSize="xs"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        w="fit-content"
                      >
                        <Icon as={StatusIcon} boxSize={3} />
                        {importJob.status}
                      </Badge>
                    </Td>
                    <Td fontSize="xs" maxW="180px">
                      <Text isTruncated fontWeight="medium" title={importJob.fileName}>
                        {importJob.fileName || 'N/A'}
                      </Text>
                      <Text color="gray.500" isTruncated>{importJob.userName || 'N/A'}</Text>
                    </Td>
                    <Td fontSize="sm">{importJob.totalRows || 0}</Td>
                    <Td fontSize="sm" fontWeight="semibold" color="green.600">
                      {importJob.importedRows || 0}
                    </Td>
                    <Td fontSize="sm" fontWeight="semibold" color={importJob.updatedRows > 0 ? 'blue.600' : 'gray.500'}>
                      {importJob.updatedRows || 0}
                    </Td>
                    <Td fontSize="sm" fontWeight="semibold" color={importJob.failedRows > 0 ? 'red.600' : 'gray.600'}>
                      {importJob.failedRows || 0}
                    </Td>
                    <Td>
                      <Tooltip label={`${progressPct}% complete`}>
                        <Progress
                          value={progressPct}
                          size="sm"
                          colorScheme={importJob.status === 'completed' ? 'green' : importJob.status === 'failed' ? 'red' : 'blue'}
                          borderRadius="full"
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td colSpan={9} p={0}>
                      <Collapse in={expandedRows.has(importJob.id)}>
                        <Box bg={expandedBG} p={4} borderTopWidth="1px" borderColor={borderColor}>
                          <VStack align="stretch" spacing={3}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" fontWeight="semibold">Import Details</Text>
                              <Text fontSize="xs" color="gray.600">
                                ID: <Code fontSize="2xs">{importJob.id}</Code>
                              </Text>
                            </HStack>

                            <HStack spacing={6} flexWrap="wrap">
                              <Box>
                                <Text fontSize="xs" color="gray.600">Total Rows</Text>
                                <Text fontSize="md" fontWeight="semibold">{importJob.totalRows}</Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.600">Created</Text>
                                <Text fontSize="md" fontWeight="semibold" color="green.600">
                                  {importJob.importedRows || 0}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.600">Updated</Text>
                                <Text fontSize="md" fontWeight="semibold" color="blue.600">
                                  {importJob.updatedRows || 0}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.600">Duplicates Skipped</Text>
                                <Text fontSize="md" fontWeight="semibold" color="orange.600">
                                  {importJob.duplicatesFound || 0}
                                </Text>
                              </Box>
                              <Box>
                                <Text fontSize="xs" color="gray.600">Success Rate</Text>
                                <Text fontSize="md" fontWeight="semibold" color="green.600">
                                  {importJob.totalRows > 0
                                    ? Math.round((((importJob.importedRows || 0) + (importJob.updatedRows || 0)) / importJob.totalRows) * 100)
                                    : 0}%
                                </Text>
                              </Box>
                            </HStack>

                            {errorList.length > 0 && (
                              <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2} color="red.600">
                                  Errors ({errorList.length})
                                </Text>
                                <Box
                                  maxH="150px"
                                  overflowY="auto"
                                  bg="white"
                                  p={2}
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                >
                                  <VStack align="stretch" spacing={1}>
                                    {errorList.slice(0, 10).map((error, idx) => (
                                      <Text key={idx} fontSize="xs" color="red.700">
                                        • {typeof error === 'string' ? error : JSON.stringify(error)}
                                      </Text>
                                    ))}
                                    {errorList.length > 10 && (
                                      <Text fontSize="xs" color="gray.500">
                                        ... and {errorList.length - 10} more
                                      </Text>
                                    )}
                                  </VStack>
                                </Box>
                              </Box>
                            )}

                            {warningList.length > 0 && (
                              <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2} color="orange.600">
                                  Warnings ({warningList.length})
                                </Text>
                                <Box
                                  maxH="150px"
                                  overflowY="auto"
                                  bg="white"
                                  p={2}
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                >
                                  <VStack align="stretch" spacing={1}>
                                    {warningList.slice(0, 10).map((warning, idx) => (
                                      <Text key={idx} fontSize="xs" color="orange.700">
                                        • {typeof warning === 'string' ? warning : JSON.stringify(warning)}
                                      </Text>
                                    ))}
                                    {warningList.length > 10 && (
                                      <Text fontSize="xs" color="gray.500">
                                        ... and {warningList.length - 10} more
                                      </Text>
                                    )}
                                  </VStack>
                                </Box>
                              </Box>
                            )}

                            <HStack fontSize="xs" color="gray.600">
                              <Text>
                                Duration:{' '}
                                {importJob.completedAt && importJob.createdAt
                                  ? `${Math.round((new Date(importJob.completedAt) - new Date(importJob.createdAt)) / 60000)} minutes`
                                  : 'In progress...'}
                              </Text>
                            </HStack>
                          </VStack>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

